import React, { useState, useContext } from 'react';
import { 
  Button, 
  TextField, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  CircularProgress,
  Snackbar,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Typography
} from '@material-ui/core';
import { AuthContext } from '../Context/AuthContext';
import axios from 'axios';
import config from '../config';

// Custom Alert component as a fallback in case @material-ui/lab is not available
function CustomAlert({ severity, onClose, children }) {
  const getBackgroundColor = () => {
    switch (severity) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      case 'success':
        return '#4caf50';
      default:
        return '#2196f3';
    }
  };

  return (
    <Paper 
      style={{ 
        backgroundColor: getBackgroundColor(),
        color: 'white',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Typography>{children}</Typography>
      {onClose && (
        <Button color="inherit" size="small" onClick={onClose}>
          CLOSE
        </Button>
      )}
    </Paper>
  );
}

// Try to import MuiAlert, but use CustomAlert as fallback
let Alert = CustomAlert;
try {
  // Dynamic import to avoid build errors if the package is not available
  const MuiAlert = require('@material-ui/lab/Alert').default;
  Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;
} catch (error) {
  console.warn('Using CustomAlert as @material-ui/lab/Alert is not available');
}

function ProfileUpdateForm({ open, handleClose, userData, onUpdateSuccess }) {
  const { user, dispatch } = useContext(AuthContext);
  const API_URL = config.API_URL;
  
  const [formData, setFormData] = useState({
    userFullName: userData?.userFullName || '',
    department: userData?.department || '',
    age: userData?.age || '',
    gender: userData?.gender || '',
    dob: userData?.dob || '',
    address: userData?.address || '',
    mobileNumber: userData?.mobileNumber || '',
    email: userData?.email || '',
    password: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    // Check if passwords match when changing password
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Validate email format
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Validate mobile number (10 digits)
    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
      setError('Mobile number must be 10 digits');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    // Remove empty fields and confirmPassword from the request
    const updateData = Object.entries(formData)
      .filter(([key, value]) => value !== '' && key !== 'confirmPassword')
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

    try {
      // Add user ID to the request
      updateData.userId = user._id;

      const response = await axios.put(
        `${API_URL}api/users/update-student-profile/${user._id}`,
        updateData
      );

      setSuccess(true);
      setLoading(false);
      
      // Update local storage with new user data
      if (response.data) {
        const updatedUser = {
          ...user,
          ...response.data
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        dispatch({ type: 'LOGIN_SUCCESS', payload: updatedUser });
      }
      
      // Notify parent component that update is complete
      if (onUpdateSuccess) {
        onUpdateSuccess(response.data);
      }

      // Close dialog after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.data) {
        setError(err.response.data);
      } else {
        setError('An error occurred while updating your profile');
      }
      console.error('Error updating profile:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" maxWidth="md" fullWidth>
        <DialogTitle id="form-dialog-title">Update Your Profile</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update your profile information below. Fields left blank will not be changed.
          </DialogContentText>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="userFullName"
                  name="userFullName"
                  label="Full Name"
                  type="text"
                  fullWidth
                  value={formData.userFullName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="department"
                  name="department"
                  label="Department"
                  type="text"
                  fullWidth
                  value={formData.department}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="age"
                  name="age"
                  label="Age"
                  type="number"
                  fullWidth
                  value={formData.age}
                  onChange={handleChange}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="dob"
                  name="dob"
                  label="Date of Birth"
                  type="date"
                  fullWidth
                  value={formData.dob}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="mobileNumber"
                  name="mobileNumber"
                  label="Mobile Number"
                  type="tel"
                  fullWidth
                  value={formData.mobileNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  id="address"
                  name="address"
                  label="Address"
                  type="text"
                  fullWidth
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  id="email"
                  name="email"
                  label="Email Address"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="password"
                  name="password"
                  label="New Password (leave blank to keep current)"
                  type="password"
                  fullWidth
                  value={formData.password}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={!formData.password}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Update Profile'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success">
          Profile updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ProfileUpdateForm; 